import { API_CONFIG } from '@/lib/api/config';
import { ApiError } from '@/lib/api/client';

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
});

export const razorpayApi = {
  // Manual connection
  async connect(payload: {
    businessId: string;
    keyId: string;
    keySecret: string;
    accountName?: string;
    webhookSecret?: string;
  }) {
    const res = await fetch(`${API_CONFIG.BASE_URL}/razorpay/connect`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to connect Razorpay');
    }
    return json;
  },

  // Disconnect
  async disconnect(businessId: string, connectionType?: 'manual' | 'oauth') {
    const url = `${API_CONFIG.BASE_URL}/razorpay/${businessId}/disconnect${connectionType ? `?connectionType=${connectionType}` : ''}`;
    const res = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to disconnect Razorpay');
    }
    return json;
  },

  // Check connection status
  async checkConnection(businessId: string) {
    const res = await fetch(`${API_CONFIG.BASE_URL}/razorpay/${businessId}/connection`, {
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to check Razorpay connection');
    }
    return json;
  },

  // OAuth - Get authorization URL
  async getOAuthUrl(businessId: string, redirectUri?: string) {
    const url = `${API_CONFIG.BASE_URL}/razorpay/oauth/authorize?businessId=${encodeURIComponent(businessId)}${redirectUri ? `&redirectUri=${encodeURIComponent(redirectUri)}` : ''}`;
    const res = await fetch(url, {
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to get OAuth URL');
    }
    return json;
  },

  // OAuth - Complete callback (API-based)
  async completeOAuthCallback(payload: {
    code: string;
    state: string;
    businessId?: string;
    redirectUri?: string;
  }) {
    const res = await fetch(`${API_CONFIG.BASE_URL}/razorpay/oauth/callback`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to complete OAuth connection');
    }
    return json;
  },

  // Refresh OAuth token
  async refreshToken(businessId: string) {
    const res = await fetch(`${API_CONFIG.BASE_URL}/razorpay/${businessId}/refresh-token`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to refresh token');
    }
    return json;
  },

  // Test connection
  async testConnection(businessId: string) {
    const res = await fetch(`${API_CONFIG.BASE_URL}/razorpay/${businessId}/test`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to test connection');
    }
    return json;
  },

  // ==================== PAYMENT PROCESSING ====================

  // Create order
  async createOrder(businessId: string, payload: {
    amount: number;
    currency?: string;
    receipt?: string;
    notes?: Record<string, any>;
    customerId?: string;
  }) {
    const res = await fetch(`${API_CONFIG.BASE_URL}/razorpay/${businessId}/orders`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to create order');
    }
    return json;
  },

  // Get order
  async getOrder(businessId: string, orderId: string) {
    const res = await fetch(`${API_CONFIG.BASE_URL}/razorpay/${businessId}/orders/${orderId}`, {
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to get order');
    }
    return json;
  },

  // List orders
  async listOrders(businessId: string, params?: {
    count?: number;
    skip?: number;
    from?: number;
    to?: number;
  }) {
    const query = new URLSearchParams();
    if (params?.count) query.append('count', params.count.toString());
    if (params?.skip) query.append('skip', params.skip.toString());
    if (params?.from) query.append('from', params.from.toString());
    if (params?.to) query.append('to', params.to.toString());
    const url = `${API_CONFIG.BASE_URL}/razorpay/${businessId}/orders${query.toString() ? `?${query.toString()}` : ''}`;
    const res = await fetch(url, {
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to list orders');
    }
    return json;
  },

  // Get payment
  async getPayment(businessId: string, paymentId: string) {
    const res = await fetch(`${API_CONFIG.BASE_URL}/razorpay/${businessId}/payments/${paymentId}`, {
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to get payment');
    }
    return json;
  },

  // List payments from database
  async listPayments(businessId: string, params?: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
  }) {
    const query = new URLSearchParams();
    // Always send page and limit with defaults
    query.append('page', (params?.page || 1).toString());
    query.append('limit', (params?.limit || 20).toString());
    if (params?.status) query.append('status', params.status);
    if (params?.search) query.append('search', params.search);
    const url = `${API_CONFIG.BASE_URL}/razorpay/${businessId}/payments${query.toString() ? `?${query.toString()}` : ''}`;
    const res = await fetch(url, {
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to list payments');
    }
    return json;
  },

  // Sync payments from Razorpay
  async syncPayments(businessId: string, params?: {
    count?: number;
    skip?: number;
  }) {
    const query = new URLSearchParams();
    if (params?.count) query.append('count', params.count.toString());
    if (params?.skip) query.append('skip', params.skip.toString());
    const url = `${API_CONFIG.BASE_URL}/razorpay/${businessId}/payments/sync${query.toString() ? `?${query.toString()}` : ''}`;
    const res = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to sync payments');
    }
    return json;
  },

  // Capture payment
  async capturePayment(businessId: string, paymentId: string, amount: number) {
    const res = await fetch(`${API_CONFIG.BASE_URL}/razorpay/${businessId}/payments/${paymentId}/capture`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ amount }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to capture payment');
    }
    return json;
  },

  // Verify payment signature
  async verifyPayment(businessId: string, payload: {
    razorpayOrderId: string;
    razorpayPaymentId: string;
    razorpaySignature: string;
  }) {
    const res = await fetch(`${API_CONFIG.BASE_URL}/razorpay/${businessId}/payments/verify`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to verify payment');
    }
    return json;
  },

  // ==================== CUSTOMER MANAGEMENT ====================

  // Create customer
  async createCustomer(businessId: string, payload: {
    name: string;
    email?: string;
    contact?: string;
    notes?: Record<string, any>;
  }) {
    const res = await fetch(`${API_CONFIG.BASE_URL}/razorpay/${businessId}/customers`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to create customer');
    }
    return json;
  },

  // Get customer
  async getCustomer(businessId: string, customerId: string) {
    const res = await fetch(`${API_CONFIG.BASE_URL}/razorpay/${businessId}/customers/${customerId}`, {
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to get customer');
    }
    return json;
  },

  // Update customer
  async updateCustomer(businessId: string, customerId: string, payload: {
    name?: string;
    email?: string;
    contact?: string;
    notes?: Record<string, any>;
  }) {
    const res = await fetch(`${API_CONFIG.BASE_URL}/razorpay/${businessId}/customers/${customerId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to update customer');
    }
    return json;
  },

  // List customers
  async listCustomers(businessId: string, params?: {
    count?: number;
    skip?: number;
  }) {
    const query = new URLSearchParams();
    if (params?.count) query.append('count', params.count.toString());
    if (params?.skip) query.append('skip', params.skip.toString());
    const url = `${API_CONFIG.BASE_URL}/razorpay/${businessId}/customers${query.toString() ? `?${query.toString()}` : ''}`;
    const res = await fetch(url, {
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to list customers');
    }
    return json;
  },

  // ==================== PAYMENT LINKS ====================

  // Create payment link
  async createPaymentLink(businessId: string, payload: {
    amount: number;
    currency?: string;
    description?: string;
    customer?: {
      name?: string;
      email?: string;
      contact?: string;
    };
    notify?: {
      sms?: boolean;
      email?: boolean;
    };
    reminder_enable?: boolean;
    callback_url?: string;
    callback_method?: 'get' | 'post';
    notes?: Record<string, any>;
    expire_by?: number;
  }) {
    const res = await fetch(`${API_CONFIG.BASE_URL}/razorpay/${businessId}/payment-links`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to create payment link');
    }
    return json;
  },

  // Get payment link
  async getPaymentLink(businessId: string, paymentLinkId: string) {
    const res = await fetch(`${API_CONFIG.BASE_URL}/razorpay/${businessId}/payment-links/${paymentLinkId}`, {
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to get payment link');
    }
    return json;
  },

  // List payment links from database
  async listPaymentLinks(businessId: string, params?: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
  }) {
    const query = new URLSearchParams();
    if (params?.page) query.append('page', params.page.toString());
    if (params?.limit) query.append('limit', params.limit.toString());
    if (params?.status) query.append('status', params.status);
    if (params?.search) query.append('search', params.search);
    const url = `${API_CONFIG.BASE_URL}/razorpay/${businessId}/payment-links${query.toString() ? `?${query.toString()}` : ''}`;
    const res = await fetch(url, {
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to list payment links');
    }
    return json;
  },

  // Sync payment links from Razorpay
  async syncPaymentLinks(businessId: string, params?: {
    count?: number;
    skip?: number;
  }) {
    const query = new URLSearchParams();
    if (params?.count) query.append('count', params.count.toString());
    if (params?.skip) query.append('skip', params.skip.toString());
    const url = `${API_CONFIG.BASE_URL}/razorpay/${businessId}/payment-links/sync${query.toString() ? `?${query.toString()}` : ''}`;
    const res = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to sync payment links');
    }
    return json;
  },

  // ==================== PLANS ====================

  // Create plan
  async createPlan(businessId: string, payload: {
    period: 'daily' | 'weekly' | 'monthly' | 'yearly';
    interval: number;
    item: {
      name: string;
      amount: number;
      currency?: string;
      description?: string;
    };
    notes?: Record<string, any>;
  }) {
    const res = await fetch(`${API_CONFIG.BASE_URL}/razorpay/${businessId}/plans`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to create plan');
    }
    return json;
  },

  // Get plan
  async getPlan(businessId: string, planId: string) {
    const res = await fetch(`${API_CONFIG.BASE_URL}/razorpay/${businessId}/plans/${planId}`, {
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to get plan');
    }
    return json;
  },

  // List plans
  async listPlans(businessId: string, params?: {
    count?: number;
    skip?: number;
  }) {
    const query = new URLSearchParams();
    if (params?.count) query.append('count', params.count.toString());
    if (params?.skip) query.append('skip', params.skip.toString());
    const url = `${API_CONFIG.BASE_URL}/razorpay/${businessId}/plans${query.toString() ? `?${query.toString()}` : ''}`;
    const res = await fetch(url, {
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to list plans');
    }
    return json;
  },

  async listOffers(businessId: string) {
    const url = `${API_CONFIG.BASE_URL}/razorpay/${businessId}/offers`;
    const res = await fetch(url, { headers: getAuthHeaders() });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to list offers');
    }
    return json;
  },

  // ==================== SUBSCRIPTIONS ====================

  // Create subscription
  async createSubscription(businessId: string, payload: {
    planId: string;
    totalCount: number;
    quantity?: number;
    customerNotify?: number;
    notes?: Record<string, any>;
    customerId?: string;
    expireBy?: number;
    offerId?: string;
  }) {
    const res = await fetch(`${API_CONFIG.BASE_URL}/razorpay/${businessId}/subscriptions`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to create subscription');
    }
    return json;
  },

  // Get subscription
  async getSubscription(businessId: string, subscriptionId: string) {
    const res = await fetch(`${API_CONFIG.BASE_URL}/razorpay/${businessId}/subscriptions/${subscriptionId}`, {
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to get subscription');
    }
    return json;
  },

  // List subscriptions
  async listSubscriptions(businessId: string, params?: {
    count?: number;
    skip?: number;
  }) {
    const query = new URLSearchParams();
    if (params?.count) query.append('count', params.count.toString());
    if (params?.skip) query.append('skip', params.skip.toString());
    const url = `${API_CONFIG.BASE_URL}/razorpay/${businessId}/subscriptions${query.toString() ? `?${query.toString()}` : ''}`;
    const res = await fetch(url, {
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to list subscriptions');
    }
    return json;
  },

  // Cancel subscription
  async cancelSubscription(businessId: string, subscriptionId: string, cancelAtCycleEnd?: boolean) {
    const res = await fetch(`${API_CONFIG.BASE_URL}/razorpay/${businessId}/subscriptions/${subscriptionId}/cancel`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ cancelAtCycleEnd }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to cancel subscription');
    }
    return json;
  },

  // Pause subscription
  async pauseSubscription(businessId: string, subscriptionId: string) {
    const res = await fetch(`${API_CONFIG.BASE_URL}/razorpay/${businessId}/subscriptions/${subscriptionId}/pause`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to pause subscription');
    }
    return json;
  },

  // Resume subscription
  async resumeSubscription(businessId: string, subscriptionId: string) {
    const res = await fetch(`${API_CONFIG.BASE_URL}/razorpay/${businessId}/subscriptions/${subscriptionId}/resume`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to resume subscription');
    }
    return json;
  },

  // Cancel payment link
  async cancelPaymentLink(businessId: string, paymentLinkId: string) {
    const res = await fetch(`${API_CONFIG.BASE_URL}/razorpay/${businessId}/payment-links/${paymentLinkId}/cancel`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to cancel payment link');
    }
    return json;
  },
};

